import type { JobName, JobPayload } from './catalogue.js';
import { type JobKeys, jobId } from './job-ids.js';
import type { RunDate } from './run-date.js';

/**
 * The jobs a schedule may start: keyed by their run date, plus whatever else
 * their static payload carries. A scheduler's template never changes between
 * occurrences, so anything in the key besides the run date must come from it.
 */
export type DailyJobName = {
  [Name in JobName]: JobKeys[Name] extends { runDate: RunDate } ? Name : never;
}[JobName];

export type ScheduleDefinition<Name extends DailyJobName = DailyJobName> = {
  id: string;
  job: Name;
  pattern: string;
  tz: string;
  payload: JobPayload<Name>;
};

export const SCHEDULES: readonly ScheduleDefinition[] = [
  {
    id: 'campaign-totals-nightly',
    job: 'campaign-totals.sweep',
    pattern: '30 3 * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
  {
    id: 'exchange-rates-afternoon',
    job: 'exchange-rates.fetch',
    pattern: '45 16 * * 1-5',
    tz: 'Europe/Istanbul',
    payload: { v: 1, slot: 'afternoon' },
  },
  {
    id: 'exchange-rates-morning',
    job: 'exchange-rates.fetch',
    pattern: '15 9 * * 1-5',
    tz: 'Europe/Istanbul',
    payload: { v: 1, slot: 'morning' },
  },
  {
    id: 'inbound-media-retention',
    job: 'inbound-media.expire',
    pattern: '15 4 * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
  {
    id: 'statement-files-retention',
    job: 'statement-import.expire',
    pattern: '45 4 * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
  {
    id: 'payment-slip-retention',
    job: 'payment-slip.expire',
    pattern: '55 4 * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
  {
    id: 'donation-import-retention',
    job: 'donation-import.expire',
    pattern: '5 5 * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
  {
    id: 'donation-attachment-retention',
    job: 'donation-attachment.expire',
    pattern: '15 5 * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
  {
    id: 'form-file-retention',
    job: 'form-file.expire',
    pattern: '30 5 * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
  {
    id: 'generated-files-purge',
    job: 'generated-files.purge',
    pattern: '45 5 * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
  {
    id: 'audit-log-payload-retention',
    job: 'audit-log.expire-payloads',
    pattern: '15 3 * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
  {
    id: 'outbox-redispatch-retention',
    job: 'outbox.purge',
    pattern: '45 2 * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
  {
    id: 'payment-pledge-sweep',
    job: 'payment.pledge-sweep',
    pattern: '15 6 * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
];

/** The jobs a recurring schedule may start: keyed by nothing, since no run of them is a dated fact. */
export type RecurringJobName = {
  [Name in JobName]: JobKeys[Name] extends Record<string, never> ? Name : never;
}[JobName];

/**
 * A schedule that fires every few minutes and keeps no ledger row: each pass
 * reads current state, so a pass missed is made up by the next one and there is
 * no run of it to catch up or to call stale.
 */
export type RecurringScheduleDefinition<Name extends RecurringJobName = RecurringJobName> = {
  id: string;
  job: Name;
  pattern: string;
  tz: string;
  payload: JobPayload<Name>;
};

/**
 * The watchdog every fifteen minutes, the generated files' expiry hourly, the
 * outbox sweep every minute and the credential reseal nightly. A reseal is what
 * makes a master-key rotation need no code change: an operator sets the new key
 * with a new id, keeps the old as `PAYMENT_CREDENTIALS_KEY_PREVIOUS`, and the
 * next pass walks every row onto the new id — docs/decisions/0053 §1. Which key
 * a row is sealed under is a state the row carries rather than a dated fact, so
 * it keeps no ledger row and a normal night moves nothing — and the same is
 * true of a file's retention, which is a state its rows carry rather than a
 * dated fact, so a pass missed is made up by the next one
 * (docs/decisions/0043-the-worker.md §7). The
 * daily schedules above are what the watchdog watches and catches up; neither
 * recurring job can watch itself, and a worker that is down runs neither — the
 * health probe on 47101 is what notices that.
 */
export const RECURRING_SCHEDULES: readonly RecurringScheduleDefinition[] = [
  {
    id: 'provider-movements-sync',
    job: 'provider-movements.sync',
    pattern: '*/15 * * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
  {
    id: 'account-transaction-equivalents-fill',
    job: 'account-transaction.equivalents-fill',
    pattern: '20,50 * * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
  {
    id: 'scheduled-jobs-watchdog',
    job: 'scheduled-jobs.watchdog',
    pattern: '*/15 * * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
  {
    id: 'generated-files-expire',
    job: 'generated-files.expire',
    pattern: '5 * * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
  {
    id: 'payment-link-expire',
    job: 'payment-link.expire',
    pattern: '35 * * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
  /**
   * The *Operasyon Sorumlusu* label, every fifteen minutes, off the watchdog's
   * minute — docs/decisions/0096. A label, not an authority, so a quarter of an
   * hour late is late on the users list and nowhere else.
   */
  {
    id: 'campaign-staff-role-sync',
    job: 'campaign-staff.role-sync',
    pattern: '7,22,37,52 * * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
  {
    id: 'payment-credentials-reseal',
    job: 'payment-credentials.reseal',
    pattern: '25 4 * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
  /**
   * The reporting read model, every five minutes —
   * docs/plans/07-notifications-and-reporting.md §7.5. Recurring rather than
   * daily because a refresh is a state the watermark carries, not a dated fact:
   * a pass missed is made up by the next one, and there is no run of it to catch
   * up or to call stale.
   */
  {
    id: 'report-rollup-refresh',
    job: 'report-rollup.refresh',
    pattern: '*/5 * * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
  {
    id: 'outbox-sweep',
    job: 'outbox.sweep',
    pattern: '* * * * *',
    tz: 'Europe/Istanbul',
    payload: { v: 1 },
  },
];

export function scheduleFor(job: JobName): ScheduleDefinition | undefined {
  return SCHEDULES.find((schedule) => schedule.job === job);
}

/** The ledger key of one scheduled run, which is also its job id. */
export function scheduledRunKey(schedule: ScheduleDefinition, runDate: RunDate): string {
  return runKeyOf(schedule.job, schedule.payload, runDate);
}

/** The run key of a delivered job: its static payload's key fields and the date it ran for. */
export function runKeyOf(job: DailyJobName, payload: object, runDate: RunDate): string {
  return jobId(job, { ...payload, runDate } as JobKeys[typeof job]);
}
